import React, { useState, useEffect } from "react";
import api from "../api";
import { useNavigate } from "react-router-dom";
import { navigateToAssessmentOrDashboard } from "../utils/assessmentNavigation";
import { Helmet } from "react-helmet";
import { useAuth } from "../context/AuthContext";
import {
  AlertCircle,
  CheckCircle2,
  FileText,
  BarChart3,
  Clock,
  Users,
  BookOpen,
  Shield,
} from "lucide-react";

const COBITInstructions = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [error] = useState("");
  const [loading, setLoading] = useState(true);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate("/login", { replace: true });
      return;
    }

    const loadPrivacyStatus = async () => {
      try {
        const res = await api.get("maturity/privacy/current/");

        setPrivacyAccepted(
          res.data?.has_notice && res.data?.accepted
        );
      } catch (err) {
        console.error("Failed to load privacy status:", err);

        // If anything goes wrong, treat as not accepted
        setPrivacyAccepted(false);
      } finally {
        setLoading(false);
      }
    };

    loadPrivacyStatus();
  }, [authLoading, user, navigate]);

  const stakeholderName = user?.name || "";
  const stakeholderEmail = user?.email || "";
  const stakeholderDepartment = user?.department || "N/A";

  const cobitDomains = [
    {
      title: "EDM — Evaluate, Direct and Monitor",
      description: "Governance structure for setting organizational direction and monitoring execution",
      practices: ["EDM01", "EDM02", "EDM03", "EDM04", "EDM05"],
      icon: "📋"
    },
    {
      title: "APO — Align, Plan and Organize",
      description: "Strategies, plans, and organizational structures for I&T management",
      practices: ["APO01", "APO02", "APO03", "APO04", "APO05", "APO06", "APO07", "APO08", "APO09", "APO10", "APO11", "APO12", "APO13", "APO14"],
      icon: "🎯"
    },
    {
      title: "BAI — Build, Acquire and Implement",
      description: "Solutions and changes to support business strategy and operations",
      practices: ["BAI01", "BAI02", "BAI03", "BAI04", "BAI05", "BAI06", "BAI07", "BAI08", "BAI09", "BAI10", "BAI11"],
      icon: "🔨"
    },
    {
      title: "DSS — Deliver, Service and Support",
      description: "Delivery and support of I&T services and operational management",
      practices: ["DSS01", "DSS02", "DSS03", "DSS04", "DSS05", "DSS06"],
      icon: "⚙️"
    },
    {
      title: "MEA — Monitor, Evaluate and Assess",
      description: "Monitoring and evaluation of I&T performance and compliance",
      practices: ["MEA01", "MEA02", "MEA03", "MEA04"],
      icon: "📊"
    },
  ];

  const steps = [
    {
      number: 1,
      title: "Understand the Process",
      description: "Read the COBIT process description to understand the context and objectives being assessed.",
      icon: <BookOpen size={24} />
    },
    {
      number: 2,
      title: "Answer Each Question",
      description: `For each governance practice, answer "Yes" (implemented) or "No" (not implemented) based on your organization's current state.`,
      icon: <CheckCircle2 size={24} />
    },
    {
      number: 3,
      title: `Provide Evidence for "Yes" Answers`,
      description: `If you answer "Yes", provide detailed evidence and attach supporting documents (policies, audit reports, process documentation, etc.).`,
      icon: <FileText size={24} />
    },
    {
      number: 4,
      title: "Submit Each Process",
      description: "Complete all questions in a process and submit it. Each process is assessed and scored independently.",
      icon: <CheckCircle2 size={24} />
    },
    {
      number: 5,
      title: "Review Your Results",
      description: "Access your dashboard to view capabilty scores for each process and identify capability gaps.",
      icon: <BarChart3 size={24} className="text-orange-600" />
    },
    {
      number: 6,
      title: "Plan Improvements",
      description: "Use the assessment results to prioritize improvements and enhance I&T governance capabilty.",
      icon: <CheckCircle2 size={24} className="text-orange-600" />
    },
  ];

  const maturityLevels = [
    { level: 0, name: "Non-Existent", description: "No processes exist", color: "bg-red-100 border-red-300" },
    { level: 1, name: "Initial", description: "0-20% implemented", color: "bg-orange-100 border-orange-300" },
    { level: 2, name: "Repeatable", description: "21-50% implemented", color: "bg-yellow-100 border-yellow-300" },
    { level: 3, name: "Defined", description: "51%+ implemented", color: "bg-lime-100 border-lime-300" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-orange-700 font-medium">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-orange-50 to-amber-50">
      <Helmet>
        <title>Instructions | COBIT 2019</title>
      </Helmet>

      <div className="bg-gradient-to-r from-orange-600 to-orange-700 text-white py-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 flex items-center gap-3">
            <BookOpen size={40} />
            COBIT 2019 Assessment Guide
          </h1>
          <p className="text-orange-100 text-lg">Complete the Enterprise I&T Governance and Management capability assessment</p>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="bg-white rounded-xl shadow-lg border-l-4 border-orange-600 p-8 mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Your Information</h2>
            <Users className="text-orange-600" size={32} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg border border-blue-200">
              <label className="text-sm font-semibold text-gray-700 block mb-2">Name</label>
              <input
                type="text"
                value={stakeholderName}
                readOnly
                className="w-full p-3 border border-blue-300 rounded-lg bg-white font-medium text-gray-900"
                aria-label="Stakeholder Name"
              />
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg border border-green-200">
              <label className="text-sm font-semibold text-gray-700 block mb-2">Email</label>
              <input
                type="email"
                value={stakeholderEmail}
                readOnly
                className="w-full p-3 border border-green-300 rounded-lg bg-white font-medium text-gray-900"
                aria-label="Stakeholder Email"
              />
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg border border-purple-200">
              <label className="text-sm font-semibold text-gray-700 block mb-2">Department</label>
              <input
                type="text"
                value={stakeholderDepartment}
                readOnly
                className="w-full p-3 border border-purple-300 rounded-lg bg-white font-medium text-gray-900"
                aria-label="Stakeholder Department"
              />
            </div>
          </div>

          {error && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-red-700 font-medium">{error}</p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-12 border-l-4 border-blue-600">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <BookOpen className="text-blue-600" size={28} />
            About This Assessment
          </h2>

          <div className="prose prose-sm max-w-none text-gray-700 space-y-4 mb-8">
            <p className="text-lg">
              Welcome to the <span className="font-bold text-orange-700">COBIT 2019 (Control Objectives for Information and Related Technologies)</span> Capability Assessment. This comprehensive evaluation measures your organization's governance and management of enterprise I&T.
            </p>

            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <p className="font-semibold text-gray-900 mb-3">What is COBIT 2019?</p>
              <p className="text-gray-700">
                COBIT 2019 is a governance and management framework that assists organizations establish and manage their I&T environment to support business objectives. It provides a comprehensive set of governance, management objectives and enabling processes across five domains covering all I&T and business functions.
              </p>
            </div>

            <p className="font-semibold text-gray-900">Assessment Goals:</p>
            <ul className="grid md:grid-cols-2 gap-3">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <span>Measure I&T governance and management capability</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <span>Identify governance, management and operational capability gaps</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <span>Support strategic I&T planning and investment decisions</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <span>Enable compliance with governance and regulatory requirements</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-12 border-l-4 border-orange-600">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-3">
            <BarChart3 className="text-orange-600" size={28} />
            COBIT 2019 Domains
          </h2>

          <div className="grid md:grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {cobitDomains.map((domain, idx) => (
              <div key={idx} className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-lg border border-gray-200 hover:shadow-md transition">
                <div className="text-4xl mb-3">{domain.icon}</div>
                <h3 className="font-bold text-gray-900 mb-2 text-lg">{domain.title}</h3>
                <p className="text-sm text-gray-600 mb-4">{domain.description}</p>
                <div className="flex flex-wrap gap-2">
                  {domain.practices.map((practice, pidx) => (
                    <span key={pidx} className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded font-semibold">
                      {practice}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-6 bg-orange-50 rounded-lg border border-orange-200">
            <p className="text-gray-700">
              <span className="font-semibold text-gray-900">Key Point:</span> Each assessment process is independent. You may save progress and continue later, or start other processes. A process can only be submitted once all questions are completed. Incomplete processes remain in <span className="font-semibold">Draft</span> status.
            </p>

            <p className="text-gray-700 mt-4">
              <span className="font-semibold text-gray-900">Who should complete:</span> Assessments should be completed by individuals with appropriate authority and knowledge, including Executive Authorities (Board), Executive Management (e.g., Chief Executive Officer (CEO), Chief Information Officer (CIO), Chief Financial Officer (CFO), Chief Risk Officer (CRO), Chief Operating Officer (COO)), Senior Managers, Process/Business Owners, and Risk, Compliance, Internal Audit, or IT Subject Matter Experts (SMEs) where applicable.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-12 border-l-4 border-green-600">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-3">
            <FileText className="text-green-600" size={28} />
            Question Components
          </h2>

          <div className="space-y-6">
            {[
              {
                label: "COBIT Domain",
                description: `The governance and management domain being assessed (e.g., "EDM — Evaluate, Direct and Monitor")`,
                example: "Domain: EDM — Evaluate, Direct and Monitor"
              },
              {
                label: "Governance / Management Objective",
                description: "The governance or management process being assessed within the selected domain (e.g., EDM01 — Ensured Governance Framework Setting and Maintenance).",
                example: "Process: EDM02 — Ensured Benefits Delivery"
              },
              {
                label: "Governance / Management Practice",
                description: "The specific governance or management practice within the selected COBIT process (e.g., EDM01.01 — Evaluate the Governance System).",
                example: "Practice: EDM02.01 — Evaluate Value Optimization"
              },
              {
                label: "Governance / Management Activity",
                description: "The key actions performed to implement and support the COBIT practice. Activities describe what is done in order to achieve the intended governance or management outcomes.",
                example: "Continually identify and engage stakeholders, document requirements, and evaluate the current and future governance design of enterprise I&T."
              },
              {
                label: "Assessment Question",
                description: "A structured question used to evaluate whether a COBIT practice and its supporting activities are implemented and effective.",
                example: "Is there a formal benefits management process for I&T investments?"
              },
              {
                label: "Your Response",
                description: `Answer "Yes" (we have this capability implemented) or "No" (we do not have this capability)`,
                example: "Yes / No"
              },
              {
                label: "Supporting Evidence",
                description: `If your answer is "Yes" you must provide detailed evidence and attach supporting documents (policies, process documents, audit reports, test results, etc.). If your answer is "No", no evidence is required.`,
                example: "Benefits management framework, I&T strategy documents, governance policies, audit reports"
              },
            ].map((item, idx) => (
              <div key={idx} className="border-l-4 border-green-400 pl-6 py-4">
                <h3 className="font-bold text-gray-900 mb-2 text-lg">{item.label}</h3>
                <p className="text-gray-700 mb-2">{item.description}</p>
                <div className="bg-green-50 p-3 rounded text-sm text-green-900 border border-green-200">
                  <span className="font-semibold">Example:</span> {item.example}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-12 border-l-4 border-purple-600">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-3">
            <BarChart3 className="text-purple-600" size={28} />
            Capability Capability Levels
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {maturityLevels.map((level, idx) => (
              <div key={idx} className={`${level.color} p-6 rounded-lg border-2`}>
                <div className="text-4xl font-bold text-gray-900 mb-2">{level.level}</div>
                <h3 className="font-bold text-gray-900 mb-1">{level.name}</h3>
                <p className="text-sm text-gray-700">{level.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 p-6 bg-purple-50 rounded-lg border border-purple-200">
            <p className="text-gray-700">
              Your responses determine the capability level for each process. The desired target is at least Level 3 (Defined) for critical governance functions.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-12 border-l-4 border-orange-600">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-3">
            <Clock className="text-orange-600" size={28} />
            How to Complete the Assessment
          </h2>

          <div className="space-y-4">
            {steps.map((step, idx) => (
              <div key={idx} className="flex gap-6 p-6 bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-200 hover:border-orange-300 transition">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-orange-100 border-2 border-orange-600">
                    <span className="text-xl font-bold text-orange-600">{step.number}</span>
                  </div>
                </div>
                <div className="flex-grow">
                  <h3 className="font-bold text-gray-900 text-lg mb-1">{step.title}</h3>
                  <p className="text-gray-700">{step.description}</p>
                </div>
                <div className="flex-shrink-0 hidden sm:flex items-center text-orange-400">
                  {step.icon}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-orange-50 rounded-xl shadow-lg p-8 mb-12 border-l-4 border-orange-600">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <AlertCircle className="text-orange-600" size={28} />
            Important Reminders
          </h2>

          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-1">
                <div className="flex items-center justify-center h-6 w-6 rounded-full bg-orange-600 text-white text-sm font-bold">•</div>
              </div>
              <p className="text-gray-700"><span className="font-semibold text-gray-900">Be Objective:</span> Answer based on what is currently adopted and implemented, not planned or aspirational capabilities.</p>
            </li>
            <li className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-1">
                <div className="flex items-center justify-center h-6 w-6 rounded-full bg-orange-600 text-white text-sm font-bold">•</div>
              </div>
              <p className="text-gray-700"><span className="font-semibold text-gray-900">Collaborate:</span> Involve I&T governance and management stakeholders to ensure accurate responses.</p>
            </li>
            <li className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-1">
                <div className="flex items-center justify-center h-6 w-6 rounded-full bg-orange-600 text-white text-sm font-bold">•</div>
              </div>
              <p className="text-gray-700"><span className="font-semibold text-gray-900">Assessment Independence:</span> Each governance and management process is assessed independently. You may save your progress and return later to complete the process or other assessments.</p>
            </li>
            <li className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-1">
                <div className="flex items-center justify-center h-6 w-6 rounded-full bg-orange-600 text-white text-sm font-bold">•</div>
              </div>
              <p className="text-gray-700"><span className="font-semibold text-gray-900">Evidence is Critical:</span> For "Yes" answers, provide comprehensive evidence to support your response.</p>
            </li>
            <li className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-1">
                <div className="flex items-center justify-center h-6 w-6 rounded-full bg-orange-600 text-white text-sm font-bold">•</div>
              </div>
              <p className="text-gray-700"><span className="font-semibold text-gray-900">Estimated Time:</span> Allocate 45-60 minutes per process depending on organizational complexity.</p>
            </li>
            <li className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-1">
                <div className="flex items-center justify-center h-6 w-6 rounded-full bg-orange-600 text-white text-sm font-bold">•</div>
              </div>
              <p className="text-gray-700"><span className="font-semibold text-gray-900">Confidentiality:</span> Your responses and personal information are treated as confidential and processed in compliance with POPIA. They are used exclusively for governance improvement purposes.</p>
            </li>
          </ul>
        </div>

        <div className="flex justify-center gap-6 mb-12 flex-wrap">

          {/* Continue Button */}
          <button
            onClick={async () => {
              if (privacyAccepted) {
                await navigateToAssessmentOrDashboard(
                  "cobit",
                  navigate
                );
              } else {
                navigate("/privacy-notice", {
                  state: {
                    framework: "cobit",
                  },
                });
              }
            }}
            className="flex items-center justify-center gap-3 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white px-8 py-4 rounded-lg font-semibold shadow-lg hover:shadow-xl transition transform hover:scale-105"
          >
            <Shield size={24} />
            <span>
              Continue to Assessment
            </span>
          </button>
        </div>

        <div className="text-center py-8 border-t border-gray-200">
          <p className="text-gray-600 mb-2">Questions or need assistance?</p>
          <p className="text-gray-500 text-sm">Contact your I&T governance administrator or refer to the COBIT 2019 reference materials.</p>
        </div>
      </main>
    </div>
  );
};

export default COBITInstructions;